import { useMemo, useState } from "react";
import { Popconfirm, message } from "antd";
import WithAuth from "../../hoc/WithAuth";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import {
  Bank,
  useActivateBank,
  useDeactivateBank,
  useGetBanks,
} from "../../api/banks";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { StatusBadge } from "../../components/shared/status-badge";
import BankModal from "../../components/modals/BankModal";

/**
 * Bank master CRUD page. Lists all banks (active + inactive) for super
 * admins and lets them create / edit / activate / deactivate.
 *
 * Banks are the anchor for every other multi-bank piece (loan configs,
 * farmer bank accounts, financing on contracts), so this page is the entry
 * point for onboarding a new financial partner.
 */

const ADMIN_ROLES = new Set(["ROLE_KFT_SUPER_ADMIN", "ROLE_SUPER_ADMIN"]);

function BanksPage() {
  const { user } = useAuth() as AuthContextValue;
  const isAdmin = !!user?.roleName && ADMIN_ROLES.has(user.roleName);

  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [open, setOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);

  const { data: banks = [], isLoading } = useGetBanks();
  const { mutateAsync: activate, isPending: activating } = useActivateBank();
  const { mutateAsync: deactivate, isPending: deactivating } =
    useDeactivateBank();

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return banks;
    return banks.filter((b) => {
      return (
        (b.bankName || "").toLowerCase().includes(term) ||
        (b.code || "").toLowerCase().includes(term) ||
        (b.swiftCode || "").toLowerCase().includes(term) ||
        (b.country || "").toLowerCase().includes(term)
      );
    });
  }, [banks, searchText]);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const columns = [
    {
      key: "bankName",
      title: "Bank name",
      dataIndex: "bankName",
      align: "left" as const,
    },
    {
      key: "code",
      title: "Code",
      dataIndex: "code",
      align: "left" as const,
      render: (v: string) => v || "-",
    },
    {
      key: "swiftCode",
      title: "SWIFT",
      dataIndex: "swiftCode",
      align: "left" as const,
      render: (v: string) => v || "-",
    },
    {
      key: "country",
      title: "Country",
      dataIndex: "country",
      align: "left" as const,
      render: (v: string) => v || "-",
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "isActive",
      align: "left" as const,
      render: (v: boolean) => (
        <StatusBadge value={v ? "ACTIVE" : "INACTIVE"} />
      ),
    },
  ];

  const actionColumns = [
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      render: (_: any, record: Bank) => (
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBank(record);
              setOpen(true);
            }}
          >
            <img
              src={Icons.pencilEdit}
              alt="edit"
              className="h-4 w-4 text-gray-100 mr-1"
            />
            Edit
          </Button>

          {record.isActive ? (
            <Popconfirm
              title="Deactivate this bank?"
              description="Existing contracts and configs are preserved but the bank will be hidden from new selections."
              okText="Deactivate"
              cancelText="Cancel"
              onConfirm={async (e) => {
                e?.stopPropagation();
                try {
                  await deactivate(record.id);
                  message.success(`${record.bankName} deactivated`);
                } catch (err: any) {
                  message.error(
                    err?.response?.data?.message ||
                      err?.message ||
                      "Deactivate failed",
                  );
                }
              }}
            >
              <Button
                variant="destructive"
                onClick={(e) => e.stopPropagation()}
                disabled={deactivating}
              >
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Button
              variant="default"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await activate(record.id);
                  message.success(`${record.bankName} activated`);
                } catch (err: any) {
                  message.error(
                    err?.response?.data?.message ||
                      err?.message ||
                      "Activate failed",
                  );
                }
              }}
              disabled={activating}
            >
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setSelectedBank(null);
    setOpen(true);
  };

  const hideModal = () => {
    setSelectedBank(null);
    setOpen(false);
  };

  return (
    <>
      <div className="hidden md:block rounded-xl bg-white">
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by name, code, SWIFT, country"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            {isAdmin && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
              >
                Create Bank
              </AppButton>
            )}
          </div>
        </div>

        <DataTable<Bank>
          data={paged}
          columns={isAdmin ? [...columns, ...actionColumns] : columns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filtered.length,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No banks found"
          rowKey="id"
        />
      </div>

      <BankModal
        open={open}
        selectedBank={selectedBank}
        hideModal={hideModal}
      />
    </>
  );
}

export default WithAuth(BanksPage);
